import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Grid, Avatar, IconButton, InputAdornment, Divider,
  CircularProgress, Autocomplete, Alert, Chip, Fade,
} from "@mui/material";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PremiumDatePicker from "../../Components/PremiumDatePicker";
import { createLead, CreateLeadResult, DuplicateCheckResult, type LeadSource } from "./leadApi";
import {
  leadFormFields, leadFormSectionLabel, splitLeadFormValues, leadConfig,
} from "./leadVertical";
import { useLeadDuplicateCheck } from "./useLeadDuplicateCheck";
import DuplicateClientCard, { pickPrimaryDuplicate } from "./DuplicateClientCard";
import { useGetCustomerQuery } from "../../Api/attoDeskApi";
import { ICustomer } from "../../Api/Interface/api.interface";
import useDebounce from "../../Components/SearchDebonce/useDebounce";

/* ── Theme ──────────────────────────────────────────────────────────────── */
const T = {
  navy: "#16233A", gold: "#7C4DFF", goldLight: "#f5eed9",
  white: "#fff", cream: "#faf8f4", bg: "#f4f2ee",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  blue: "#2563eb", green: "#16a34a", red: "#dc2626",
} as const;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called on success. `mergedLeadId` is set when an existing lead was
   *  matched (duplicate detection). `created` carries the new lead so
   *  the parent can optimistically render it before the refetch returns
   *  — otherwise the table looks unchanged until the next poll. */
  onCreated: (mergedLeadId?: number, created?: any) => void;
  defaultSource?: LeadSource;
  defaultCustomer?: ICustomer | null;
}

// Event-Type / hall / guest-count / budget lists moved to `leadVertical.ts`
// so autism / venue / tutoring each render the appropriate manual-create
// fields via `leadFormFields`. The venue vertical still gets the original
// wedding / corporate / birthday / hall / guests / budget UI; autism gets
// program / child / grade / location / preferred-schedule inputs that
// write to `custom_fields` instead of the dormant venue columns.

const SOURCE_OPTIONS = [
  { value: "website",  label: "Website" },
  { value: "email",    label: "Email" },
  { value: "phone",    label: "Phone" },
  { value: "walk_in",  label: "Walk-in" },
  { value: "referral", label: "Referral" },
  { value: "manual",   label: "Manual" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{10}$/;

function stripNonDigits(p: string): string {
  return p.replace(/\D/g, "");
}

function validatePhone(p: string): string | undefined {
  if (!p) return "Phone number is required";
  if (p.length < 10) return `Must be 10 digits (${p.length}/10)`;
  if (p.length > 10) return "Phone must be exactly 10 digits";
  if (!PHONE_RE.test(p)) return "Phone must contain digits only";
  return undefined;
}

function validateEmail(e: string): string | undefined {
  if (!e) return undefined;
  if (!EMAIL_RE.test(e)) return "Enter a valid email address";
  return undefined;
}

interface FormState {
  name: string; email: string; phone: string; organization: string;
  notes: string;
  source: LeadSource;
  /** Vertical-specific field values keyed by `column:<name>` /
   *  `custom:<slug>` — see leadVertical.leadFormFields. */
  vertical: Record<string, string>;
}

interface FieldErrors { name?: string; email?: string; phone?: string }

const emptyVerticalValues = (): Record<string, string> =>
  Object.fromEntries(leadFormFields.map(f => [f.key, ""]));

const BLANK: FormState = {
  name: "", email: "", phone: "", organization: "",
  notes: "",
  source: "manual",
  vertical: emptyVerticalValues(),
};

export default function CreateLeadDialog({ open, onClose, onCreated, defaultSource = "manual", defaultCustomer }: Props) {
  const [form, setForm] = useState<FormState>({ ...BLANK, source: defaultSource });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mergeNotice, setMergeNotice] = useState<{ leadId: number; message: string } | null>(null);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<ICustomer | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const debouncedClientSearch = useDebounce(clientSearch, 300);

  /* ── Smart auto-link state ────────────────────────────────────────
   * `autoLinkedClientId` is set when the duplicate detector matched a
   * SINGLE existing client and we programmatically selected it. Drives
   * the success state on the Select Client field, the "Existing Client
   * Linked" chip, and the auto_linked flag we send on save (so the
   * timeline records WHY the link exists).
   *
   * `manualClientPick` flips true the moment staff explicitly chooses
   * a client from the Autocomplete (or one is passed via defaultCustomer).
   * It pauses the auto-link effect so we never fight a deliberate pick —
   * staff stays in control. Clearing the dropdown resets it. */
  const [autoLinkedClientId, setAutoLinkedClientId] = useState<number | null>(null);
  const [manualClientPick, setManualClientPick] = useState(false);

  // Real-time duplicate awareness — fires per field as soon as the
  // input is "checkable" (10 digits or valid email format). We keep
  // the hook ENABLED even when customerId is set — that way changing
  // the phone after an auto-link is detected by the unlink effect.
  // The only thing that pauses it is a MANUAL client pick.
  const { phoneState: dupPhone, emailState: dupEmail, reset: resetDup } =
    useLeadDuplicateCheck(form.phone, form.email, { enabled: open && !manualClientPick });

  useEffect(() => {
    if (open && defaultCustomer) {
      setSelectedClient(defaultCustomer);
      setCustomerId(defaultCustomer.id);
      // A caller-supplied default counts as a manual pick — the hook
      // shouldn't try to override it based on the prefilled phone/email.
      setManualClientPick(true);
      setClientSearch(`${defaultCustomer.customerFirstName} ${defaultCustomer.customerLastName}`.trim());
      setForm((prev) => ({
        ...prev,
        name: `${defaultCustomer.customerFirstName} ${defaultCustomer.customerLastName}`.trim(),
        email: (defaultCustomer.customerEmail || "").trim().toLowerCase(),
        phone: stripNonDigits(defaultCustomer.customerMobileNum || ""),
        organization: defaultCustomer.customerCompanyName || "",
      }));
    }
    if (!open) {
      setSelectedClient(null);
      setCustomerId(null);
      setAutoLinkedClientId(null);
      setManualClientPick(false);
      setClientSearch("");
      setForm({ ...BLANK, source: defaultSource });
      setErrors({});
      resetDup();
      setSaveError(null);
      setMergeNotice(null);
    }
  }, [open, defaultCustomer, defaultSource, resetDup]);

  /* ── Pick the best single-client match for auto-linking ───────────
   * Auto-link ONLY fires when the duplicate detector returned exactly
   * one client. Multiple matches surface a "Multiple matching clients
   * found" hint instead — the operator picks the right one from the
   * dropdown. A lead match is never enough to auto-link (lead != client). */
  const singleClientMatch = useMemo<DuplicateCheckResult | null>(() => {
    const both: DuplicateCheckResult[] = [];
    if (dupPhone.match) both.push(dupPhone.match);
    if (dupEmail.match) both.push(dupEmail.match);
    return both.find((m) => m.type === "client" && (m.client_count ?? 1) === 1) ?? null;
  }, [dupPhone.match, dupEmail.match]);

  const ambiguousClientMatch = useMemo(() => {
    return [dupPhone.match, dupEmail.match].some(
      (m) => m?.type === "client" && (m.client_count ?? 1) > 1,
    );
  }, [dupPhone.match, dupEmail.match]);

  /* ── Auto-link effect ─────────────────────────────────────────────
   * Reacts to the dup-detection state. When a single client appears:
   *   • Mark that client as the active selection
   *   • Autofill any EMPTY contact field with the matched values
   *     (spec: "ONLY autofill if field is empty"). We never overwrite
   *     a value the user typed.
   * Guarded by `autoLinkedClientId` so the same client only triggers
   * one autofill pass — re-clearing a field manually doesn't refill it. */
  useEffect(() => {
    if (!open || manualClientPick) return;
    if (!singleClientMatch?.id) return;
    if (autoLinkedClientId === singleClientMatch.id) return;

    const m = singleClientMatch;
    setAutoLinkedClientId(m.id ?? null);
    setCustomerId(m.id ?? null);

    // Synthesize an ICustomer shape from the dup payload so the
    // Autocomplete and its label render exactly as if the user had
    // picked the row themselves. The full record is on the server;
    // we don't need every column to drive the modal's UI.
    const nameParts = (m.name || "").trim().split(/\s+/);
    const synthetic: ICustomer = {
      id: m.id!,
      customerFirstName: nameParts.shift() || "",
      customerLastName:  nameParts.join(" ") || "",
      customerMobileNum: m.phone || "",
      customerEmail:     m.email || "",
      customerCompanyName: m.organization || "",
    } as ICustomer;
    setSelectedClient(synthetic);
    setClientSearch(m.name || "");

    setForm((prev) => ({
      ...prev,
      name:         prev.name.trim() === ""         ? (m.name || "")                                        : prev.name,
      email:        prev.email.trim() === ""        ? (m.email || "").trim().toLowerCase()                  : prev.email,
      phone:        prev.phone.trim() === ""        ? stripNonDigits(m.phone || "").slice(0, 10)            : prev.phone,
      organization: prev.organization.trim() === "" ? (m.organization || "")                                : prev.organization,
    }));
  }, [open, manualClientPick, singleClientMatch, autoLinkedClientId]);

  /* ── Unlink effect ────────────────────────────────────────────────
   * When the phone or email is edited away from the auto-linked client,
   * the dup detector will eventually return null. We release the
   * customer binding so the lead saves as a brand-new customer — the
   * operator stays in control of the link. Fields they've already
   * edited STAY edited; we only unwire the customer side. */
  useEffect(() => {
    if (!open) return;
    if (manualClientPick) return;
    if (!autoLinkedClientId) return;
    if (singleClientMatch?.id === autoLinkedClientId) return;
    // Match is gone or has flipped to a different record — release.
    setAutoLinkedClientId(null);
    setCustomerId(null);
    setSelectedClient(null);
    setClientSearch("");
  }, [open, manualClientPick, singleClientMatch, autoLinkedClientId]);

  const { data: customerData } = useGetCustomerQuery(
    { searchText: debouncedClientSearch, id: "" },
    { skip: debouncedClientSearch.length < 2 }
  );
  const customers: ICustomer[] = Array.isArray(customerData?.data) ? (customerData?.data as ICustomer[]) : [];

  const handleClientSelect = (customer: ICustomer | null) => {
    setSelectedClient(customer);
    // Manual selection wins over auto-link. Once the user explicitly
    // chooses (or explicitly clears) the dropdown, the duplicate hook
    // pauses so we don't fight them. Clearing the picker re-arms the
    // hook for whatever phone/email they type next.
    setAutoLinkedClientId(null);
    if (customer) {
      setManualClientPick(true);
      setCustomerId(customer.id);
      setForm((prev) => ({
        ...prev,
        name: `${customer.customerFirstName} ${customer.customerLastName}`.trim(),
        email: (customer.customerEmail || "").trim().toLowerCase(),
        phone: stripNonDigits(customer.customerMobileNum || ""),
        organization: customer.customerCompanyName || "",
      }));
      setErrors({});
      resetDup();
    } else {
      setManualClientPick(false);
      setCustomerId(null);
    }
  };

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let val = e.target.value;
    if (field === "phone") {
      val = stripNonDigits(val).slice(0, 10);
      setForm((prev) => ({ ...prev, phone: val }));
      setErrors((prev) => ({ ...prev, phone: validatePhone(val) }));
      return;
    }
    if (field === "email") {
      val = val.trimStart().toLowerCase();
      setForm((prev) => ({ ...prev, email: val }));
      setErrors((prev) => ({ ...prev, email: validateEmail(val.trim()) }));
      return;
    }
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  // Blur handlers are kept so the duplicate check still triggers when
  // the user tabs out of a partially-valid value — the hook itself
  // gates on "checkable" (10 digits / valid email format), so blurring
  // a 9-digit phone simply runs the format validation without an API
  // call. The API trigger is handled internally by the hook.
  const handleEmailBlur = () => {
    setErrors((prev) => ({ ...prev, email: validateEmail(form.email.trim()) }));
  };

  const handlePhoneBlur = () => {
    setErrors((prev) => ({ ...prev, phone: validatePhone(form.phone) }));
  };

  const validate = (): boolean => {
    const errs: FieldErrors = {};
    if (!form.name.trim()) errs.name = "Name is required";
    errs.phone = validatePhone(form.phone);
    errs.email = validateEmail(form.email.trim());
    const clean: FieldErrors = Object.fromEntries(
      Object.entries(errs).filter(([, v]) => v !== undefined)
    );
    setErrors(clean);
    return Object.keys(clean).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { columns, customFields } = splitLeadFormValues(form.vertical);
      const payload: any = {
        name: form.name,
        email: form.email.trim().toLowerCase() || null,
        phone: form.phone,
        organization: form.organization,
        notes: form.notes,
        source: form.source,
        customer_id: customerId,
        // Vertical-appropriate top-level columns (venue vertical only).
        // Autism / tutoring get an empty `columns` object here — their
        // program / child / location values ride under `custom_fields`
        // below and never leak into event_type / preferred_hall /
        // guest_count / budget_value.
        ...columns,
        // Structured custom_fields blob consumed by the backend
        // LeadInquiryNormalizer + createLeadWithContext. Kept absent
        // (rather than sent empty) when no autism fields were filled in
        // so we don't spawn empty inquiry rows.
        ...(Object.keys(customFields).length ? { custom_fields: customFields } : {}),
        // Surface to the timeline that this lead was auto-linked to an
        // existing client by the duplicate-detection flow.
        auto_linked: autoLinkedClientId !== null,
      };
      // Always use the authenticated staff endpoint regardless of the
      // chosen source. The source dropdown is just metadata (how the
      // customer originally found us); routing staff-portal submissions
      // through the anonymous /leads/inquiry endpoint stripped the auth
      // context, so the timeline was logging "System / Website inquiry
      // received from <name>" for every lead a staff member created
      // with source=website. Now every staff-portal create attributes
      // to the logged-in user. The public website form (separate
      // EventInquiryController route) is the only thing that should
      // ever land in createFromInquiry → "System" actor.
      const result: CreateLeadResult = await createLead(payload);

      // Reset form state
      setForm({ ...BLANK, source: defaultSource });
      setSelectedClient(null);
      setClientSearch("");
      setCustomerId(null);
      setAutoLinkedClientId(null);
      setManualClientPick(false);
      setErrors({});
      resetDup();

      if (result.merged && result.existingId) {
        // Show inline notice — user clicks "View Lead" to dismiss and open the existing lead
        setMergeNotice({
          leadId: result.existingId,
          message: result.message ?? "Lead already exists. Inquiry has been attached to their record.",
        });
        setSaving(false);
        return;
      }

      // Normal new-lead path. Hand the parent the freshly-created row
      // so it can optimistically prepend it to the list — otherwise
      // the table appears unchanged until the next refetch.
      onCreated(undefined, result?.data);
    } catch (err: any) {
      const msg =
        err?.message ||
        (err?.errors ? Object.values(err.errors).flat().join(" ") : null) ||
        "Failed to create lead. Please try again.";
      setSaveError(msg);
    }
    setSaving(false);
  };

  const handleViewMergedLead = () => {
    if (!mergeNotice) return;
    const id = mergeNotice.leadId;
    setMergeNotice(null);
    onCreated(id);
  };

  const nameInitial = form.name.trim() ? form.name.trim()[0].toUpperCase() : "+";
  const hasErrors = Object.keys(errors).some((k) => !!(errors as any)[k]);

  /* ── Shared field style ── */
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      fontSize: 13.5, borderRadius: "6px",
      "& fieldset": { borderColor: "#dde1e8" },
      "&:hover fieldset": { borderColor: "#94a3b8" },
      "&.Mui-focused fieldset": { borderColor: T.navy, borderWidth: 1.5 },
    },
    "& .MuiInputLabel-root": { fontSize: 13 },
    "& .MuiFormHelperText-root": { fontSize: 11, mt: "3px" },
  };

  const sectionLabel = (text: string) => (
    <Typography sx={{
      fontSize: 10.5, fontWeight: 700, color: T.slateLt,
      textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5,
    }}>
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px", overflow: "hidden",
          boxShadow: "0 20px 60px rgba(22,35,58,0.2)",
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${T.navy} 0%, #1a3354 100%)`,
        px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2,
      }}>
        <Avatar sx={{
          width: 44, height: 44, bgcolor: T.gold,
          fontSize: 18, fontWeight: 700, color: T.navy,
          boxShadow: "0 4px 12px rgba(124,77,255,0.35)",
        }}>
          {nameInitial}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.white, lineHeight: 1.2 }}>
            New Lead
          </Typography>
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)", mt: 0.2 }}>
            Add a new prospect to the pipeline
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: T.white, bgcolor: "rgba(255,255,255,0.08)" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>

        {/* ── Alerts ──
            Duplicate awareness now lives INLINE under each field (see
            phone + email helperText below). Top-of-modal alerts are
            reserved for save-time errors + post-save merge notices,
            which are operational events, not field-level warnings. */}
        {(saveError || mergeNotice) && (
          <Box sx={{ px: 3, pt: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
            {saveError && (
              <Alert severity="error" sx={{ borderRadius: "8px", fontSize: "0.78rem", py: 0.75 }}>
                {saveError}
              </Alert>
            )}
            {mergeNotice && (
              <Alert severity="info" sx={{ borderRadius: "8px", fontSize: "0.78rem", py: 0.75 }}
                action={
                  <Button size="small" onClick={handleViewMergedLead}
                    sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap", color: T.blue }}>
                    View Lead
                  </Button>
                }>
                {mergeNotice.message}
              </Alert>
            )}
          </Box>
        )}

        {/* ── SELECT CLIENT ── */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          {sectionLabel("Select Client")}
          <Autocomplete
            options={customers}
            getOptionLabel={(c) =>
              `${c.customerFirstName} ${c.customerLastName}` + (c.customerMobileNum ? ` · ${c.customerMobileNum}` : "")
            }
            value={selectedClient}
            onChange={(_, val) => handleClientSelect(val)}
            inputValue={clientSearch}
            onInputChange={(_, val) => setClientSearch(val)}
            filterOptions={(x) => x}
            size="small"
            renderOption={(props, c) => (
              <Box component="li" {...props} key={c.id} sx={{ gap: 1.5 }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: T.navy, flexShrink: 0 }}>
                  {(c.customerFirstName || "?").charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                    {c.customerFirstName} {c.customerLastName}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: T.slateLt }}>
                    {c.customerMobileNum}{c.customerEmail ? ` · ${c.customerEmail}` : ""}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name or phone number..."
                /* When the Select Client field is bound via auto-link,
                 * paint the outline a subtle CRM-success green so the
                 * operator instantly sees the row was filled in by the
                 * system, not by them. Manual picks keep the default
                 * navy-on-focus styling so the two states stay visually
                 * distinguishable. */
                sx={{
                  ...fieldSx,
                  ...(autoLinkedClientId
                    ? {
                        "& .MuiOutlinedInput-root": {
                          ...((fieldSx as any)["& .MuiOutlinedInput-root"]),
                          "& fieldset": { borderColor: T.green, borderWidth: 1.5 },
                          "&:hover fieldset": { borderColor: T.green },
                          "&.Mui-focused fieldset": { borderColor: T.green },
                          bgcolor: "#f0fdf4",
                        },
                      }
                    : null),
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        {autoLinkedClientId
                          ? <CheckCircleRoundedIcon sx={{ fontSize: 18, color: T.green }} />
                          : <SearchIcon sx={{ fontSize: 17, color: T.slateLt }} />}
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {/* Status row beneath the Autocomplete — two distinct states:
             • Auto-link: green chip + tiny "auto-linked via match" hint
             • Manual pick: gold "Client linked" text (existing UX)
             Renders nothing when no client is selected. */}
          {selectedClient && (
            <Fade in timeout={220}>
              <Box sx={{
                mt: 0.75, display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap",
              }}>
                {autoLinkedClientId ? (
                  <>
                    <Chip
                      size="small"
                      icon={<CheckCircleRoundedIcon sx={{ fontSize: 12 }} />}
                      label="Existing Client Linked"
                      sx={{
                        height: 20, fontSize: 10.5, fontWeight: 700,
                        bgcolor: "#dcfce7", color: "#166534",
                        letterSpacing: 0.3,
                        "& .MuiChip-icon": { color: "#16a34a", ml: 0.4 },
                      }}
                    />
                    <Typography sx={{ fontSize: 11, color: "#166534", fontWeight: 500 }}>
                      Auto-detected from phone/email match
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 11.5, color: T.gold, fontWeight: 500 }}>
                    Client linked — details pre-filled below
                  </Typography>
                )}
              </Box>
            </Fade>
          )}
        </Box>

        <Divider sx={{ borderColor: T.line }} />

        {/* ── CONTACT INFORMATION ── */}
        <Box sx={{ px: 3, pt: 2, pb: 2 }}>
          {sectionLabel("Contact Information")}

          <TextField
            label="Full Name *"
            size="small"
            fullWidth
            value={form.name}
            onChange={set("name")}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ ...fieldSx, mb: 2 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Email Address"
                size="small"
                fullWidth
                value={form.email}
                onChange={set("email")}
                onBlur={handleEmailBlur}
                // Format error is the ONLY thing that paints this field
                // red. Duplicate hits render as an awareness card below
                // the row (DuplicateClientCard) — they're informational,
                // not blocking, so they don't share the validation slot.
                error={!!errors.email}
                helperText={errors.email}
                InputProps={dupEmail.loading ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CircularProgress size={13} sx={{ color: T.slateLt }} />
                    </InputAdornment>
                  ),
                } : undefined}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Phone Number *"
                size="small"
                fullWidth
                value={form.phone}
                onChange={set("phone")}
                onBlur={handlePhoneBlur}
                error={!!errors.phone}
                helperText={
                  errors.phone
                    ? errors.phone
                    : form.phone
                    ? `${form.phone.length} digits`
                    : undefined
                }
                inputProps={{ inputMode: "numeric" }}
                InputProps={dupPhone.loading ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CircularProgress size={13} sx={{ color: T.slateLt }} />
                    </InputAdornment>
                  ),
                } : undefined}
                sx={fieldSx}
              />
            </Grid>
          </Grid>

          {/* Awareness card — renders when phone OR email resolves to
              an existing client/lead. Three visual states, mutually
              exclusive:
                • auto-linked (green) — a single client matched and we
                  auto-selected it above
                • ambiguous (red, no link) — multiple clients matched,
                  operator picks from the dropdown
                • plain warning (red) — lead match or single-client
                  match the operator hasn't yet committed to
              Sits in the row's flow so it never overlaps another field. */}
          <DuplicateClientCard
            match={pickPrimaryDuplicate(dupPhone.match, dupEmail.match)}
            autoLinked={!!autoLinkedClientId}
            ambiguous={ambiguousClientMatch && !autoLinkedClientId}
          />

          <TextField
            label="Organization"
            size="small"
            fullWidth
            value={form.organization}
            onChange={set("organization")}
            sx={{ ...fieldSx, mt: 2 }}
          />
        </Box>

        <Divider sx={{ borderColor: T.line }} />

        {/* ── INQUIRY DETAILS (vertical-driven) ──
            Field list resolved from leadVertical.leadFormFields:
              • autism_services / tutoring → program, child, location,
                grade, preferred days, preferred start (all persisted
                to lead_inquiries.custom_fields — never to venue cols)
              • venue → event type, event date, guests, budget, hall
            Source dropdown always renders at the end so both verticals
            capture how the lead arrived. */}
        <Box sx={{ px: 3, pt: 2, pb: 2 }}>
          {sectionLabel(leadFormSectionLabel)}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {leadFormFields.map((f) => {
              const value = form.vertical[f.key] ?? "";
              const onChange = (val: string) =>
                setForm((p) => ({ ...p, vertical: { ...p.vertical, [f.key]: val } }));
              const cols = f.gridColumns ?? 6;
              if (f.type === "select") {
                return (
                  <Grid key={f.key} item xs={cols}>
                    <FormControl size="small" fullWidth sx={fieldSx}>
                      <InputLabel sx={{ fontSize: 13 }}>{f.label}</InputLabel>
                      <Select
                        value={value}
                        label={f.label}
                        onChange={(e) => onChange(String(e.target.value))}
                        sx={{ fontSize: 13.5 }}
                      >
                        {(f.options ?? []).map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13.5 }}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                );
              }
              if (f.type === "date") {
                return (
                  <Grid key={f.key} item xs={cols}>
                    <PremiumDatePicker
                      label={f.label}
                      value={value}
                      onChange={(val) => onChange(val)}
                      size="small"
                    />
                  </Grid>
                );
              }
              return (
                <Grid key={f.key} item xs={cols}>
                  <TextField
                    label={f.label}
                    type={f.type === "number" ? "number" : "text"}
                    multiline={f.type === "textarea"}
                    rows={f.type === "textarea" ? 3 : undefined}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={f.placeholder}
                    inputProps={f.type === "number" ? { min: 0 } : undefined}
                    sx={fieldSx}
                  />
                </Grid>
              );
            })}

            <Grid item xs={leadConfig.supportsEventFields ? 4 : 6}>
              <FormControl size="small" fullWidth sx={fieldSx}>
                <InputLabel sx={{ fontSize: 13 }}>Source</InputLabel>
                <Select
                  value={form.source}
                  label="Source"
                  onChange={(e) => setForm((p) => ({ ...p, source: e.target.value as LeadSource }))}
                  sx={{ fontSize: 13.5 }}
                >
                  {SOURCE_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value} sx={{ fontSize: 13.5 }}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ borderColor: T.line }} />

        {/* ── NOTES ── */}
        <Box sx={{ px: 3, pt: 2, pb: 2.5 }}>
          {sectionLabel("Notes")}
          <TextField
            placeholder="Add any additional notes about this lead..."
            multiline
            rows={3}
            size="small"
            fullWidth
            value={form.notes}
            onChange={set("notes")}
            sx={{
              ...fieldSx,
              "& .MuiOutlinedInput-root": {
                ...((fieldSx as any)["& .MuiOutlinedInput-root"]),
                bgcolor: "#fafbfc",
              },
            }}
          />
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{
        px: 3, py: 2,
        bgcolor: "#f8f9fb",
        borderTop: `1px solid ${T.line}`,
        gap: 1,
      }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none", fontWeight: 600, fontSize: 13,
            color: T.slate, px: 2.5, borderRadius: "6px",
            "&:hover": { bgcolor: "#eef0f4" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !form.name.trim() || hasErrors}
          startIcon={saving
            ? <CircularProgress size={15} sx={{ color: T.white }} />
            : <PersonAddOutlinedIcon sx={{ fontSize: 17 }} />
          }
          sx={{
            textTransform: "none", fontWeight: 700, fontSize: 13,
            px: 3, borderRadius: "6px",
            bgcolor: T.navy,
            boxShadow: "0 2px 8px rgba(22,35,58,0.2)",
            "&:hover": { bgcolor: "#1a3354", boxShadow: "0 4px 14px rgba(22,35,58,0.3)" },
            "&.Mui-disabled": { bgcolor: "#c5cdd8", color: T.white },
          }}
        >
          {saving ? "Creating..." : "Create Lead"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

